"use client"

import { useEffect, useMemo, useState } from "react"

import {
  canCatalogVisibilityDifferForChannel,
  getCatalogVisibilityMode,
  shouldResolveCatalogVisibility,
} from "@/lib/catalog-visibility"
import { getCategoryLinkByName } from "@/lib/link-utils/link-utils"
import { DEFAULT_MARKET_CHANNEL, type MarketChannel } from "@/lib/market/market-channel"
import { Category } from "@/lib/storefront/category/category"
import { cn } from "@/lib/utils"
import CategoryCardNew from "@/components/search-and-category/CategoryCardNew"
import type { CategoryCard as CategoryCardData } from "@/components/storyblok/UbCategoryResults"

interface StorefrontContextResponse {
  oci: boolean
  isAnonymous: boolean
}

function determineSubcategoryCards(selectedCategory: Category, sbLocale: string) {
  return selectedCategory?.subCategories.map((subCategory: Category): CategoryCardData => {
    return {
      name: subCategory.name,
      link: getCategoryLinkByName(subCategory.name, undefined, sbLocale),
      img: subCategory.image_url || "",
      subCategories: subCategory?.subCategories,
    }
  })
}

function CategoryCard(props: {
  name: string
  img: string
  link: string
  showImage?: boolean
  showSubcategories?: boolean
  subCategories?: Category[]
}) {
  return (
    <a
      href={props.link}
      className={cn("group flex h-20 w-full flex-col rounded-lg border border-gray-200 bg-white", {
        "h-72 transition duration-200 ease-in-out hover:z-150 hover:scale-105":
          props.showImage && !props.showSubcategories,
        "h-auto min-h-72 transition duration-200 ease-in-out hover:z-150 hover:scale-105":
          props.showImage && props.showSubcategories,
      })}
    >
      {props.showImage && props.img && props.img !== "" && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          alt={props.name}
          className={cn("hidden h-48 w-full rounded-t-lg object-cover object-top", { block: props.showImage })}
          src={props.img}
        />
      )}
      <div className={"flex h-full items-center justify-center p-2"}>
        <p className={"text-center text-lg/tight font-bold group-hover:text-primary"}>{props.name}</p>
      </div>
      {props.showSubcategories && (
        <div className={"flex flex-col gap-1 p-2"}>
          {props?.subCategories?.map((sc: Category) => (
            <span key={sc.id}>{sc.name}</span>
          ))}
        </div>
      )}
    </a>
  )
}

function CategorySubcategoriesSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={index} className="min-h-72 animate-pulse rounded-lg bg-white shadow-lg">
          <div className="h-48 rounded-t-lg bg-gray-200" />
          <div className="space-y-3 p-5">
            <div className="h-5 w-3/4 rounded-sm bg-gray-200" />
            <div className="h-4 w-1/2 rounded-sm bg-gray-100" />
            <div className="h-4 w-2/3 rounded-sm bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  )
}

export default function CategorySubcategoriesSectionClient({
  initialCategory,
  sbLocale,
  channel = DEFAULT_MARKET_CHANNEL,
  categoryUrlSlug,
  newLayout,
  isTopLevelCategory,
  isSecondLevelCategory,
  showSubCategoryImages,
  disableImages,
  disableNestedSubcategories,
}: {
  initialCategory: Category
  sbLocale: string
  channel?: MarketChannel
  categoryUrlSlug: string
  newLayout: boolean
  isTopLevelCategory: boolean
  isSecondLevelCategory: boolean
  showSubCategoryImages?: boolean
  disableImages?: boolean
  disableNestedSubcategories?: boolean
}) {
  const initialShouldFetchTree = canCatalogVisibilityDifferForChannel(getCatalogVisibilityMode(channel), channel)
  const [category, setCategory] = useState(initialCategory)
  const [effectiveChannel, setEffectiveChannel] = useState<MarketChannel>(channel)
  const [effectiveIsAnonymous, setEffectiveIsAnonymous] = useState(true)
  const [contextLoaded, setContextLoaded] = useState(false)
  const [loading, setLoading] = useState(initialShouldFetchTree)

  useEffect(() => {
    setEffectiveChannel(channel)

    setContextLoaded(false)
    const controller = new AbortController()
    const contextSearchParams = new URLSearchParams()
    contextSearchParams.set("sbLocale", sbLocale)
    contextSearchParams.set("channel", channel)
    const contextUrl = `/api/storefront/context?${contextSearchParams.toString()}`

    fetch(contextUrl, {
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : undefined))
      .then((context: StorefrontContextResponse | undefined) => {
        setEffectiveChannel(context?.oci ? "oci" : channel)
        setEffectiveIsAnonymous(context?.isAnonymous ?? true)
        setContextLoaded(true)
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          console.error("Could not load storefront context for category tree", error)
        }
        setEffectiveChannel(channel)
        setEffectiveIsAnonymous(true)
        setContextLoaded(true)
      })

    return () => controller.abort()
  }, [channel, sbLocale])

  useEffect(() => {
    if (!contextLoaded) {
      return
    }

    const catalogVisibilityMode = getCatalogVisibilityMode(effectiveChannel)
    const shouldFetchTree = shouldResolveCatalogVisibility({
      mode: catalogVisibilityMode,
      channel: effectiveChannel,
      isAuthenticated: !effectiveIsAnonymous,
    })

    if (!shouldFetchTree) {
      setCategory(initialCategory)
      setLoading(false)
      return
    }

    const controller = new AbortController()
    const apiSearchParams = new URLSearchParams()
    apiSearchParams.set("sbLocale", sbLocale)
    apiSearchParams.set("channel", effectiveChannel)
    apiSearchParams.set("categorySlug", categoryUrlSlug)
    const treeUrl = `/api/storefront/categories/tree?${apiSearchParams.toString()}`
    setLoading(true)

    fetch(treeUrl, {
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : undefined))
      .then((response: { category?: Category; personalized?: boolean } | undefined) => {
        if (!response) {
          setCategory(initialCategory)
          return
        }

        setCategory(response.category ?? { ...initialCategory, subCategories: [] })
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          console.error("Could not load personalized category tree", error)
        }
        setCategory(initialCategory)
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      })

    return () => controller.abort()
  }, [categoryUrlSlug, contextLoaded, effectiveChannel, effectiveIsAnonymous, initialCategory, sbLocale])

  const subcategories = useMemo(() => determineSubcategoryCards(category, sbLocale), [category, sbLocale])
  const showSubCategoriesInNewLayout =
    (isTopLevelCategory || isSecondLevelCategory) && subcategories.length > 0 && newLayout

  if (loading) {
    return <CategorySubcategoriesSkeleton />
  }

  if (subcategories.length === 0) {
    return null
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4">
      {subcategories.map((subCategory) =>
        newLayout ? (
          <CategoryCardNew
            key={subCategory.name}
            categoryCard={subCategory}
            showImage={!disableImages && (showSubCategoriesInNewLayout || showSubCategoryImages)}
            showSubcategories={
              !disableNestedSubcategories && newLayout && (isTopLevelCategory || isSecondLevelCategory)
            }
            sbLocale={sbLocale}
          />
        ) : (
          <CategoryCard
            key={subCategory.name}
            name={subCategory.name}
            img={subCategory.img}
            link={subCategory.link}
            showImage={!disableImages && (isTopLevelCategory || showSubCategoryImages)}
            showSubcategories={!disableNestedSubcategories && newLayout && isTopLevelCategory}
            subCategories={subCategory.subCategories}
          />
        )
      )}
    </div>
  )
}
